/**
 * Iter 194 — Escalation Heat-map card for Portfolio Pulse.
 *
 * A 30-day matrix showing which projects triggered which escalation
 * levels. Cells colour-code by max level fired that day; hover a cell
 * to see the specific tasks that blew SLA.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { ShieldAlert, Flame } from "lucide-react";

export default function EscalationHeatmapCard({ wsId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [hoverCell, setHoverCell] = useState(null);

  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    api.get(`/workspaces/${wsId}/portfolio/escalation-heatmap`,
              { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(apiErrorMessage(e, "Failed to load heat-map")); setData(null); })
      .finally(() => setLoading(false));
  }, [wsId, days]);

  if (loading || !data) return (
    <div className="border border-border bg-card p-3 text-xs text-muted-foreground"
              data-testid="escalation-heatmap-card">
      Loading escalation heat-map…
    </div>
  );

  const isQuiet = data.total_events === 0;

  return (
    <div className="border border-border bg-card" data-testid="escalation-heatmap-card">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 bg-muted/20">
        <Flame className="w-3.5 h-3.5 text-rose-600" />
        <span className="text-[10px] uppercase tracking-widest font-semibold">
          Escalation heat-map
        </span>
        <span className="text-[10px] text-muted-foreground">
          Which projects trigger the most SLA breaches
        </span>
        <div className="ml-auto flex items-center gap-1">
          {[7, 30, 90].map((w) => (
            <button key={w} type="button" onClick={() => setDays(w)}
                      className={`h-6 px-2 text-[10px] border ${days === w ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
                      data-testid={`heatmap-window-${w}`}>
              {w}d
            </button>
          ))}
        </div>
      </div>

      {isQuiet ? (
        <div className="p-6 text-center text-xs text-muted-foreground italic">
          <ShieldAlert className="w-6 h-6 mx-auto mb-2 opacity-40" />
          No escalation events in the last {days} days — everyone&apos;s shipping on time.
        </div>
      ) : (
        <div className="p-3 overflow-x-auto">
          <table className="text-[10px] border-collapse">
            <thead>
              <tr>
                <th className="text-left p-1 sticky left-0 bg-card z-10 min-w-[180px]">
                  Project
                </th>
                <th className="text-center p-1 min-w-[40px]" title="Total events">Σ</th>
                {data.days_axis.map((d) => (
                  <th key={d} className="text-center p-0.5 font-mono text-muted-foreground"
                        title={d}
                        style={{ minWidth: 16 }}>
                    {d.slice(-2)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.project_id} className="border-t border-border/40"
                        data-testid={`heatmap-row-${row.project_id}`}>
                  <td className="p-1 sticky left-0 bg-card z-10 font-medium truncate max-w-[180px]"
                          title={row.project_name}>
                    {row.project_name}
                  </td>
                  <td className="p-1 text-center font-mono text-[10px]">
                    <span className={`inline-block px-1 border ${row.max_level >= 3 ? "border-rose-500 bg-rose-50 text-rose-800" : row.max_level >= 2 ? "border-amber-500 bg-amber-50 text-amber-800" : "border-sky-500 bg-sky-50 text-sky-800"}`}>
                      {row.total}
                    </span>
                  </td>
                  {data.days_axis.map((d) => {
                    const cell = row.cells[d];
                    const isHover = hoverCell?.pid === row.project_id
                                            && hoverCell?.date === d;
                    return (
                      <td key={d} className="p-0.5 text-center relative">
                        <div className={`w-full h-4 border border-border cursor-help ${_cellTone(cell)}`}
                                onMouseEnter={() => cell.count > 0 && setHoverCell({ pid: row.project_id, date: d, cell, projectName: row.project_name })}
                                onMouseLeave={() => setHoverCell(null)}
                                title={cell.count > 0
                                  ? `${cell.count} event(s) · max L${cell.max_level}`
                                  : ""}
                                data-testid={`heatmap-cell-${row.project_id}-${d}`}>
                          {cell.count > 0 && (
                            <span className="text-[8px] text-white font-bold">
                              {cell.count > 9 ? "9+" : cell.count}
                            </span>
                          )}
                        </div>
                        {isHover && cell.tasks.length > 0 && (
                          <div className="absolute z-30 top-full mt-1 left-1/2 -translate-x-1/2 w-64 border border-border bg-white shadow-lg p-2 text-left"
                                  data-testid="heatmap-hover-tasks">
                            <div className="text-[9px] uppercase tracking-widest font-semibold mb-1 text-muted-foreground">
                              {row.project_name} · {d}
                            </div>
                            {cell.tasks.map((tk, i) => (
                              <div key={i} className="text-[10px] py-0.5 border-b border-border/40 last:border-b-0">
                                <span className="font-mono font-bold text-rose-700 mr-1">L{tk.level}</span>
                                <span className="font-medium">{tk.title}</span>
                                {typeof tk.hours_overdue === "number" && (
                                  <span className="text-muted-foreground"> · {Math.round(tk.hours_overdue)}h late</span>
                                )}
                                {tk.actor && (
                                  <span className="text-muted-foreground"> · to {tk.actor}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center gap-3 mt-2 text-[9px] text-muted-foreground">
            <span>Cell shade:</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-sky-500 inline-block" /> L1</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 inline-block" /> L2</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-rose-600 inline-block" /> L3+</span>
            <span className="ml-auto">Hover a cell to see the tasks that fired.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function _cellTone(cell) {
  if (!cell || !cell.count) return "bg-muted/20";
  const lvl = cell.max_level;
  if (lvl >= 3) return "bg-rose-600";
  if (lvl >= 2) return "bg-amber-500";
  return "bg-sky-500";
}
